import React, { useEffect, useMemo, useState } from 'react';
import { generateLivePreviewUrl } from 'helpers/thumbnails';
import { Show } from 'components/Functional';
import CartImage from 'components/CartItemList/CartImage';
import { CartItemProjectPreview } from 'components/CartItemList/CartItem/CartItemPreview/CartItemProjectPreview';
import useProjects, { CompactProject } from 'hooks/useProjects';
import { useSplitTreatment } from '../../../../hooks/splits/useSplitTreatment';
import { SPLIT_IO_FEATURE_FLAG_CART_PREVIEW_1, SPLIT_IO_FEATURE_FLAG_CART_PREVIEW_1_COOKIE } from 'constants/split';

export interface CartItemPreviewProps {
  projectId: string;
  project: CompactProject;
  flashId: string;
  flashToken: string;
  imgUrl: string;
  productAttributes: { [key: string]: string };
  livePreviewAttributes: string;
  sku: string;
  hammerCategory: string;
  isEditable: boolean;
  isDeviceSmall: boolean;
  projectVersion: '1' | '2';
  children?: React.ReactElement;
  handleItemClick: () => void;
  editorUrl: string;
}

export const CartItemPreview = ({
  projectId,
  project,
  flashId,
  flashToken,
  isEditable,
  livePreviewAttributes,
  sku,
  hammerCategory,
  productAttributes,
  imgUrl,
  isDeviceSmall,
  projectVersion,
  children,
  handleItemClick,
  editorUrl
}: CartItemPreviewProps): JSX.Element => {
  const [thumbnailUrl, setThumbnailUrl] = useState('');

  const { editProjectHandler } = useProjects(flashId, flashToken, undefined, false);

  const { treatmentStatus: showCartPreview1Split } = useSplitTreatment(
    SPLIT_IO_FEATURE_FLAG_CART_PREVIEW_1,
    SPLIT_IO_FEATURE_FLAG_CART_PREVIEW_1_COOKIE
  );

  useEffect(() => {
    if (!isEditable && !livePreviewAttributes) {
      setThumbnailUrl(imgUrl);
    } else if (livePreviewAttributes) {
      setThumbnailUrl(
        generateLivePreviewUrl(
          process.env.GATSBY_LIVE_PREVIEW_URL,
          sku,
          livePreviewAttributes,
          productAttributes,
          imgUrl
        )
      );
    } else if (imgUrl) {
      setThumbnailUrl(imgUrl);
    }
  }, [flashId, flashToken, imgUrl, isEditable, livePreviewAttributes, productAttributes, projectId, sku, thumbnailUrl]);

  const cartItemProjectPreview = useMemo(() => {
    const projectVersion = project?.version;

    return (
      <CartItemProjectPreview
        projectId={projectId}
        projectVersion={projectVersion}
        flashId={flashId}
        fallbackThumbnail={thumbnailUrl}
        handleItemClick={showCartPreview1Split ? editProjectHandler(project, editorUrl) : handleItemClick}
      />
    );
    // TODO: Refactor this dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, flashId, thumbnailUrl, project]);

  return (
    <>
      <Show when={projectVersion === '2'}>{cartItemProjectPreview}</Show>
      <Show when={projectVersion !== '2'}>
        <Show when={!!thumbnailUrl}>
          <CartImage productImgUrl={thumbnailUrl} alt={hammerCategory} handleItemClick={handleItemClick}>
            {children}
          </CartImage>
        </Show>
      </Show>
    </>
  );
};
